import { format, parseISO, subDays } from "date-fns";

const DISPLAY_DATE_FORMAT = "dd/MM/yyyy";
const ISO_DATE_FORMAT = "yyyy-MM-dd";
const DEFAULT_PERIOD_DAYS = 7;

/**
 * Default filter window of Conciliação de Depósitos: the current week, modelled
 * as the last 7 days up to today (`yyyy-MM-dd`), mirroring the store list filter.
 * Used to rebuild the period query params when they are missing and to recover
 * from an invalid range.
 */
export function currentWeekPeriod(): { periodStart: string; periodEnd: string } {
  const today: Date = new Date();
  return {
    periodStart: format(subDays(today, DEFAULT_PERIOD_DAYS), ISO_DATE_FORMAT),
    periodEnd: format(today, ISO_DATE_FORMAT),
  };
}

/**
 * Formats an ISO `yyyy-MM-dd` period as a user-facing PT-BR label
 * (`dd/MM/yyyy a dd/MM/yyyy`). Returns `null` while the range is incomplete and
 * collapses to a single date when both ends match.
 */
export function formatPeriodLabel(start?: string, end?: string): string | null {
  if (!start || !end) return null;

  const startLabel: string = format(parseISO(start), DISPLAY_DATE_FORMAT);
  if (start === end) return startLabel;

  const endLabel: string = format(parseISO(end), DISPLAY_DATE_FORMAT);
  return `${startLabel} a ${endLabel}`;
}

/**
 * A period is only valid when both ends are present, the start is not after the
 * end and the end is not in the future. Sending an incongruent range to the API
 * makes the backend return a 500, so the query must not fire until this is true.
 */
export function isValidPeriod(start?: string, end?: string): boolean {
  const hasBoth: boolean = !!start && !!end;
  if (!hasBoth) return false;

  const today: string = format(new Date(), "yyyy-MM-dd");
  const validRange: boolean = start! <= end!;
  const validEnd: boolean = end! <= today;
  return validRange && validEnd;
}

/**
 * Inline error message (PT-BR, user-facing) for an incongruent period, mirroring
 * the wording used in the reprocess modal. Returns `null` when the range is valid
 * or still incomplete, so the caller can render it conditionally.
 */
export function periodRangeError(start?: string, end?: string): string | null {
  const hasBoth: boolean = !!start && !!end;
  if (!hasBoth) return null;

  const today: string = format(new Date(), "yyyy-MM-dd");
  if (start! > end!) return "Data fim deve ser maior ou igual à data início.";
  if (end! > today) return "Data fim não pode ser no futuro.";
  return null;
}

export interface IsoPeriod {
  start: string;
  end: string;
}

export interface PickerRange {
  from: Date;
  to: Date;
}

const ISO_DATE_LENGTH = 10;

const fromIso = (iso: string): Date => {
  const [year, month, day] = iso.slice(0, ISO_DATE_LENGTH).split("-").map(Number);

  return new Date(year, month - 1, day);
};

const toIso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

export const toPickerRange = (period: IsoPeriod): PickerRange | undefined => {
  if (!period.start || !period.end) return undefined;

  return { from: fromIso(period.start), to: fromIso(period.end) };
};

export const toIsoPeriod = (range?: PickerRange): IsoPeriod =>
  range ? { start: toIso(range.from), end: toIso(range.to) } : { start: "", end: "" };
